import { Button, Collapse, Fab, Typography, Zoom } from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  useTheme
} from '@material-ui/core/styles';
import { CreateNewFolderRounded } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { RootStateOrAny, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RRLink } from 'react-router-dom';
import { getProject } from '../../api/projects';
import { RootState } from '../../app/store';
import { ORDER } from '../../common/enum/order';
import EnhancedTableToolbar from '../../components/EnhancedTableToolbar';
import ProjectCard from '../../components/ProjectCard';
import getNotFoundText from '../../functions/getNotFoundText';
import { getAllBookmarks, getBookmarks } from '../../functions/manageBookmark';
import { getCookie, updateCookie } from '../../functions/manageCookies';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import { Project as ProjectType, getProjectsAsync } from './projectSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    bookmarkHeader: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 16,
      padding: theme.spacing(1, 0, 1, 1)
    },
    projectHeader: {
      marginTop: 11
    },
    fileNotFound: {
      padding: theme.spacing(10, 2),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    flexGrow: {
      flexGrow: 1
    },
    paddingNone: {
      padding: 0
    },
    paddingNoneTB: {
      paddingTop: 0,
      paddingBottom: 0
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    },
    textTransformNone: {
      textTransform: 'none'
    },
    extendedIcon: {
      marginRight: theme.spacing(1)
    },
    actionIcon: {
      position: 'fixed',
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 100,
      paddingRight: theme.spacing(3)
    }
  })
);

interface ProjectParam {
  page: number;
  limit: number;
  sortBy?: string;
  orderBy?: ORDER;
  search?: string | null;
}

// Note: sortOptions will be created inside component to use translation

export default function Project() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const sortOptions = [
    { value: 'id', text: t('projects.sortOptions.createdDate') },
    { value: 'projectNo', text: t('projects.sortOptions.projectNo') },
    { value: 'name', text: t('projects.sortOptions.projectName') }
  ];

  const { user } = useSelector((state: RootState) => state.userProfile);
  const { data: projects, count } = useSelector(
    (state: RootStateOrAny) => state.projects
  );

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const pageKey = `[DOC]-userId${user?.id}-allProjects`;
  const cookieParams: ProjectParam | undefined = getCookie(pageKey);

  const defaultParams: ProjectParam = {
    page: 0,
    limit: 30,
    sortBy: 'id',
    orderBy: ORDER.desc,
    search: null
  };

  const [params, setParams] = useState<ProjectParam>({
    ...defaultParams,
    ...cookieParams
  });
  const [bookmarkProjectIds, setBookmarkProjectIds] = useState<number[]>(
    getBookmarks(user ? user.id : undefined)
  );
  const [allBookmarks, setAllBookmarks] = useState<
    {
      userId: number;
      projectIds: number[];
    }[]
  >(getAllBookmarks());
  const [bookmarkProjects, setBookmarkProjects] = useState<ProjectType[]>([]);

  useEffect(() => {
    async function fetchProjects() {
      loadingProject(params);
      const projects = await Promise.all(
        bookmarkProjectIds.map(
          async (bookmarkProjectId) => await getProject(bookmarkProjectId)
        )
      );
      setBookmarkProjects(projects);
    }
    fetchProjects();
  }, []);

  async function loadingProject(params: ProjectParam) {
    updateCookie(pageKey, params);
    dispatch(getProjectsAsync(params));
    setParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingProject({
      ...params,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function handleBookmark(project: ProjectType, isBookmark: boolean) {
    if (bookmarkProjectIds) {
      const otherBookmarks = allBookmarks.filter(
        (allBookmark) => allBookmark.userId !== user?.id
      );
      if (!isBookmark) {
        const myBookmark = {
          userId: user?.id,
          projectIds: [...bookmarkProjectIds, project.id]
        };
        const allNewBookmark = [...otherBookmarks, myBookmark];
        localStorage.setItem('bookmarks', JSON.stringify(allNewBookmark));
        setBookmarkProjects([...bookmarkProjects, project]);
      } else {
        const myOtherProjectIds = bookmarkProjectIds.filter(
          (bookmarkProjectId) => bookmarkProjectId !== project.id
        );
        const myBookmark = { userId: user?.id, projectIds: myOtherProjectIds };
        const allNewBookmark = [...otherBookmarks, myBookmark];
        if (myOtherProjectIds.length === 0) {
          localStorage.setItem('bookmarks', JSON.stringify(otherBookmarks));
        } else {
          localStorage.setItem('bookmarks', JSON.stringify(allNewBookmark));
        }
        const myOtherProjects = bookmarkProjects.filter(
          (bookmarkProject) => bookmarkProject.id !== project.id
        );
        setBookmarkProjects(myOtherProjects);
      }
    }
    setBookmarkProjectIds(getBookmarks(user ? user.id : undefined));
    setAllBookmarks(getAllBookmarks());
  }

  function RemoveAllBookmarks() {
    setBookmarkProjects([]);
    const otherBookmarks = allBookmarks.filter(
      (allBookmark) => allBookmark.userId !== user?.id
    );
    localStorage.setItem('bookmarks', JSON.stringify(otherBookmarks));
    setBookmarkProjectIds(getBookmarks(user ? user.id : undefined));
    setAllBookmarks(getAllBookmarks());
  }

  return (
    <div className={classes.root}>
      <Breadcrumb name="projectDocument" />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projects.title')}
      </Typography>
      <Collapse in={Boolean(bookmarkProjects && bookmarkProjects.length > 0)}>
        <div className={classes.bookmarkHeader}>
          <Typography
            className={classes.flexGrow}
            variant="body1"
            color="textSecondary"
          >
            {t('projects.bookmarks')}
          </Typography>
          <Button
            className={classes.textTransformNone}
            variant="outlined"
            size="small"
            onClick={RemoveAllBookmarks}
          >
            {t('projects.removeAllBookmarks')}
          </Button>
        </div>
        <ProjectCard
          renderProjects={bookmarkProjects}
          bookmarkProjectIds={bookmarkProjectIds}
          handleBookmark={handleBookmark}
        />
      </Collapse>
      <div className={classes.projectHeader}>
        <EnhancedTableToolbar
          name={t('projects.title')}
          params={params}
          sortOptions={sortOptions}
          loadingElement={loadingProject}
          canSearchable
        />
      </div>
      <Typography
        className={classes.marginBottom}
        variant="body2"
        color="textSecondary"
      >
        {t('common.about')} {count} {count > 1 ? t('common.results') : t('common.result')}
      </Typography>
      {projects.length > 0 ? (
        <ProjectCard
          renderProjects={projects}
          bookmarkProjectIds={bookmarkProjectIds}
          handleBookmark={handleBookmark}
        />
      ) : (
        <div className={classes.fileNotFound}>
          {getNotFoundText(params.search, t('common.project') as string, t('common.projects') as string, t)}
        </div>
      )}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / params.limit)}
          shape="rounded"
          page={params.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.actionIcon}
          component={RRLink}
          to="/projects/create"
          variant="extended"
          color="primary"
          aria-label="add-project"
        >
          <CreateNewFolderRounded className={classes.extendedIcon} />
          {t('projects.setUpNewProject')}
        </Fab>
      </Zoom>
    </div>
  );
}
